import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import TrackInfoRow from './TrackInfoRow';
import TrackInfoRowsLoading from './TrackInfoRowsLoading';
import { loadTrack } from './audioHelper';
import AudioPlayerDrawer from './AudioPlayerDrawer';

const PodcastEpisodeList = ({ podcastId, sortOrder }) => {
    const [trackInfos, setTrackInfos] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [initialError, setInitialError] = useState(false);
    const [retry, setRetry] = useState(0);
    const [canLoadMore, setCanLoadMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadMoreFailed, setLoadMoreFailed] = useState(false);
    const active = useRef(true);
    const pageRequestPending = useRef(false);
    const pageCursor = useRef(null);
    const lastElemObserver = useRef(null);

    useEffect(() => {
        let currentRequest = true;
        active.current = true;
        setInitialLoading(true);
        setInitialError(false);
        axios.post('/podcast_tracks', { podcastId, onlyEpisodesWithAI: false, sortOrder })
            .then((res) => {
                if (!currentRequest) return;
                setTrackInfos(res.data.trackInfos);
                pageCursor.current = res.data.trackInfos.at(-1) ?? null;
                setCanLoadMore(res.data.trackInfos.length > 0);
            })
            .catch((err) => {
                if (currentRequest) setInitialError(true);
            })
            .finally(() => {
                if (currentRequest) setInitialLoading(false);
            });
        return () => {
            currentRequest = false;
            active.current = false;
            lastElemObserver.current?.disconnect();
            lastElemObserver.current = null;
        };
    }, [podcastId, sortOrder, retry]);

    const [showAudioPlayerDrawer, setShowAudioPlayerDrawer] = useState(false);
    const trackInfoRef = useRef(null);

    // Connected rows can reuse their render when another page or drawer updates.
    const playTrack = useCallback((trackInfo, startSeconds) => () => {
        trackInfoRef.current = trackInfo;
        loadTrack(trackInfo.mp3URL, startSeconds);
        setShowAudioPlayerDrawer(true);
    }, []);

    const loadMore = useCallback(() => {
        // Observer callbacks can arrive again before React commits loadingMore.
        if (!active.current || pageRequestPending.current || !canLoadMore || !pageCursor.current) return;
        pageRequestPending.current = true;
        lastElemObserver.current?.disconnect();
        lastElemObserver.current = null;
        setLoadingMore(true);
        setLoadMoreFailed(false);
        const { publishedDate: lastPublished, id: lastTrackId } = pageCursor.current;
        axios.post('/podcast_tracks', { podcastId, onlyEpisodesWithAI: false, sortOrder, lastPublished, lastTrackId })
            .then((res) => {
                if (!active.current) return;
                const nextTracks = res.data.trackInfos;
                const ids = new Set(trackInfos.map(track => track.id));
                const newTracks = nextTracks.filter(track => {
                    if (ids.has(track.id)) return false;
                    ids.add(track.id);
                    return true;
                });
                // Preserve the server boundary even when a repeated episode is removed above.
                const nextCursor = nextTracks.at(-1);
                setCanLoadMore(!!nextCursor && (
                    nextCursor.publishedDate !== lastPublished || nextCursor.id !== lastTrackId
                ));
                if (nextCursor) pageCursor.current = nextCursor;
                if (newTracks.length > 0) setTrackInfos(current => current.concat(newTracks));
            })
            .catch((err) => {
                if (!active.current) return;
                console.log(err);
                setLoadMoreFailed(true);
            })
            .finally(() => {
                pageRequestPending.current = false;
                if (active.current) setLoadingMore(false);
            });
    }, [canLoadMore, podcastId, sortOrder, trackInfos]);

    const lastElementRef = useCallback(node => {
        lastElemObserver.current?.disconnect();
        lastElemObserver.current = null;
        if (!node || loadingMore || loadMoreFailed || !canLoadMore) return;
        const observer = new IntersectionObserver(entries => {
            if (lastElemObserver.current === observer && entries.some(entry => entry.isIntersecting)) loadMore();
        });
        lastElemObserver.current = observer;
        observer.observe(node);
    }, [loadingMore, loadMoreFailed, canLoadMore, loadMore]);

    return (
        <div>
            <AudioPlayerDrawer
                open={showAudioPlayerDrawer}
                onClose={() => setShowAudioPlayerDrawer(false)}
                source={trackInfoRef.current?.source ?? null}
                trackId={trackInfoRef.current?.id ?? null} />
            {initialLoading ? (
                <TrackInfoRowsLoading hideSource includesVerticalCardPadding={false} />
            ) : initialError ? (
                <div role='alert'>
                    <p>Couldn't load episodes.</p>
                    <button type='button' onClick={() => setRetry(value => value + 1)}>Try again</button>
                </div>
            ) : (
                <div className='trackInfoRowsVerticalList'>
                    {trackInfos.map((trackInfo, index) => (
                        <TrackInfoRow
                            key={trackInfo.id}
                            hideSource
                            showEpisodeArtwork
                            trackInfo={trackInfo}
                            playTrack={playTrack}
                            includesVerticalCardPadding={false}
                            showsBottomDivider={index !== trackInfos.length - 1}
                        />
                    ))}
                    {trackInfos.length > 0 && canLoadMore && (
                        <div ref={lastElementRef} style={{ fontSize: '14px', fontWeight: '500', padding: '30px', textAlign: 'center' }}>
                            {loadMoreFailed ? (
                                <>
                                    <div>Couldn't load more episodes.</div>
                                    <button type='button' onClick={loadMore}>Try again</button>
                                </>
                            ) : 'Loading more...'}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Changing shows or order resets pagination and isolates late responses from
// the newly selected list.
const PodcastEpisodes = ({ podcastId, sortOrder = 'newest' }) => (
    <PodcastEpisodeList key={`${podcastId}:${sortOrder}`} podcastId={podcastId} sortOrder={sortOrder} />
);

export default PodcastEpisodes;
